<template>
  <popup @close-popup="closePopup">
    <template v-slot:content>
      <div class="payment">
        <h1 class="title">How do you want to pay?</h1>
        <p class="subtitle">Kindly select your preferred payment plan</p>

        <div class="payment-form">
          <div>
            <custom-input @input-event="getTenureValue" input-name="payment" input-type="radio" input-value="3" input-id="3-months">
              <template v-slot:label-content>
                <span>
                  Pay in 3 months from
                  <span>{{
                    moneyFormatter(threeMonthsRepaymentSchedule[1]?.amountDue / 100, {
                      countryCode: 'ng',
                      showEmptyDecimal: false,
                      displayCurrencyAs: 'symbol',
                      officialLanguage: 'en'
                    })
                  }}</span>
                  per month
                </span>
              </template>
            </custom-input>
          </div>
          <div>
            <custom-input @input-event="getTenureValue" input-name="payment" input-type="radio" input-value="6" input-id="6-months">
              <template v-slot:label-content>
                <span>
                  Pay in 6 months from
                  <span>{{
                    moneyFormatter(sixMonthsRepaymentSchedule[1]?.amountDue / 100, {
                      countryCode: 'ng',
                      showEmptyDecimal: false,
                      displayCurrencyAs: 'symbol',
                      officialLanguage: 'en'
                    })
                  }}</span>
                  per month
                </span>
              </template>
            </custom-input>
          </div>
        </div>

        <div class="payment-checkout">
          <div class="payment-checkout_grid">
            <div class="payment-checkout_grid__title">Total Cost</div>
            <div class="payment-checkout_grid__alignRight">
              {{
                moneyFormatter(store.state.zeroWidgetProps.totalPrice / 100, {
                  countryCode: 'ng',
                  showEmptyDecimal: false,
                  displayCurrencyAs: 'symbol',
                  officialLanguage: 'en'
                })
              }}
            </div>
          </div>

          <div class="message--error" v-if="store.state.errorMessage">{{ store.state.errorMessage }}</div>
          <div class="btn btn__primary btn--lg">
            <button :disabled="!userSelectedTenure || store.state.apiCallTracker.isError" @click="paymentPlanCheckout">Checkout</button>
          </div>
        </div>
      </div>
    </template>
  </popup>
</template>

<script lang="ts" setup>
import { onBeforeMount, ref } from 'vue'

import CustomInput from '@/components/custom-radio-or-check-input/index.vue'

import popup from '@/components/cards/popup/index.vue'
import store from '@/composables/useZeroWidgetStore/methods'

import { RepaymentScheduleInterface, ScheduleInterface } from '@/models/composables'
import { moneyFormatter } from '@/composables/useUtilities'

const threeMonthsRepaymentSchedule = ref<Array<ScheduleInterface>>([
  { amountDue: 0, amountPaid: 0, dueDate: '', late: false, paidDate: '', principalPaid: '', state: '' },
  { amountDue: 0, amountPaid: 0, dueDate: '', late: false, paidDate: '', principalPaid: '', state: '' }
])
const sixMonthsRepaymentSchedule = ref<Array<ScheduleInterface>>([
  { amountDue: 0, amountPaid: 0, dueDate: '', late: false, paidDate: '', principalPaid: '', state: '' },
  { amountDue: 0, amountPaid: 0, dueDate: '', late: false, paidDate: '', principalPaid: '', state: '' }
])
const userSelectedTenure = ref(0)

const errorValue = ref('')

const getTenureValue = (target: HTMLInputElement) => {
  userSelectedTenure.value = parseInt(target.value)
  userSelectedTenure.value === 3
    ? store.actions.setRepaymentSchedule(threeMonthsRepaymentSchedule.value)
    : store.actions.setRepaymentSchedule(sixMonthsRepaymentSchedule.value)
}

onBeforeMount(() => {
  const repaymentQueryParamsFor3Months: RepaymentScheduleInterface = {
    amount: store.state.zeroWidgetProps.totalPrice,
    duration: 3,
    repaymentInterval: 1,
    unit: 'MONTH'
  }
  const repaymentQueryParamsFor6Months: RepaymentScheduleInterface = {
    amount: store.state.zeroWidgetProps.totalPrice,
    duration: 6,
    repaymentInterval: 1,
    unit: 'MONTH'
  }
  const threeMonthsData = store.actions.getRepaymentSchedule(repaymentQueryParamsFor3Months)
  const sixMonthsData = store.actions.getRepaymentSchedule(repaymentQueryParamsFor6Months)
  Promise.all([threeMonthsData, sixMonthsData]).then((result) => {
    threeMonthsRepaymentSchedule.value = result[0]?.data?.list || [{}, {}]

    sixMonthsRepaymentSchedule.value = result[1]?.data?.list || [{}, {}]
  })
})

const emit = defineEmits<{
  (e: 'close-popup'): void
}>()
const closePopup = () => {
  return emit('close-popup')
}

const paymentPlanCheckout = () => {
  if (!userSelectedTenure.value) {
    errorValue.value = `You've not selected a tenure`
    return
  }
  store.actions.setComponent('payment-structure')
}
</script>
